import { LocalStorageStore, LocalStorageContext } from 'lib/context/app'
import { useContext, useState, useEffect, useCallback } from 'react'

/**
 * Similar to useLocalStorage, but creates a global context for the store, so that the value
 * can be synced across multiple components.
 *
 * @param key - The key to store the value under
 * @param defaultValue - The default value to use if the key is not found in the context store or localStorage
 * @returns A tuple containing the current value and a function to update it
 */
export function useLocalStorageContext<K extends keyof LocalStorageStore>(
    key: K,
    defaultValue: LocalStorageStore[K],
): [LocalStorageStore[K], (value: LocalStorageStore[K]) => void] {
    const { store, setStore } = useContext(LocalStorageContext)

    const [value, setValue] = useState<LocalStorageStore[K]>(() => {
        // If the context store already has a value for this key:
        if (store[key] !== undefined) {
            return store[key]
        }

        // Otherwise, look in localStorage
        const lsItem = localStorage.getItem(key)
        if (lsItem !== null) {
            return JSON.parse(lsItem)
        }

        // Otherwise, set it to the provided default value
        return defaultValue
    })

    useEffect(() => {
        if (store[key] !== undefined) {
            setValue(store[key])
        }
    }, [store, key, defaultValue])

    const setSyncedValue = useCallback(
        (newValue: LocalStorageStore[K]) => {
            // Update LocalStorage
            localStorage.setItem(key, JSON.stringify(newValue))

            // Update this item's value in the context store
            setStore((prev) => ({ ...prev, [key]: newValue }))

            // Update the local value of this hook
            setValue(newValue)
        },
        [key, setStore],
    )

    return [value, setSyncedValue]
}
